import React, { useState, useEffect } from "react";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Card,
  CardBody,
  Text,
  VStack,
  HStack,
  Icon,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Skeleton,
  Badge,
  Spinner,
  Center,
  Button,
  useToast,
} from "@chakra-ui/react";
import {
  FiServer,
  FiUsers,
  FiHardDrive,
  FiCode,
  FiMail,
  FiDollarSign,
  FiUserPlus,
  FiLock,
  FiPackage,
  FiList,
  FiShoppingCart,
  FiCalendar,
  FiClock,
  FiFileText,
  FiHome,
  FiBriefcase,
  FiSettings,
  FiDatabase,
  FiAlertCircle,
  FiHeadphones,
  FiBarChart2,
  FiTrendingUp,
  FiPrinter
} from "react-icons/fi";
import { NavbarWithCallToAction } from "../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { gql, useQuery } from "@apollo/client";
import { motion } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { LoginModal } from "../components/LoginModal";

interface NavItem {
  path: string;
  title: string;
  description: string;
  icon: React.ComponentType;
  comingSoon?: boolean;
}

interface NavGroup {
  title: string;
  subtitle?: string;
  items: NavItem[];
}

interface NavCardProps {
  item: NavItem;
}

const navigationGroups: Record<string, NavGroup> = {


  businessCalendar: {
    title: "🗓️ Business Command Center - Start Here",
    subtitle: "Get clear on what you're doing with your central planning hub",
    items: [
      {
        path: "/business-calendar",
        title: "Master Business Calendar",
        description: "Your command center - plan everything from client meetings to project deadlines in one place",
        icon: FiCalendar,
        comingSoon: true
      },
      {
        path: "/business-calendar/events",
        title: "Strategic Events Planner",
        description: "Plan networking events, workshops, and business milestones to grow your influence",
        icon: FiCalendar,
        comingSoon: true
      },
    ],
  },




  projects: {
    title: "🚀 Project Collaboration - Achieve Together",
    subtitle: "Work with clients on meaningful projects where you can achieve something impactful together",
    items: [
      {
        path: "/projects",
        title: "All Active Projects",
        description: "Track progress on client projects - from websites to blockchain implementations",
        icon: FiBriefcase,
      },
      {
        path: "/projects/new",
        title: "Start New Project",
        description: "Begin a new collaboration with a client - define scope, timeline, and deliverables",
        icon: FiBriefcase,
      },
    ],
  },

  
  sessions: {
    title: "📅 Booking & Networking - Build Your Network",
    subtitle: "Create valuable connections through strategic booking sessions and events",
    items: [
      {
        path: "/sessions/types",
        title: "Session Types",
        description: "Define consultation types, discovery calls, and service offerings to attract ideal clients",
        icon: FiList,
      },
      {
        path: "/sessions/types/create",
        title: "Create New Session Type",
        description: "Design new ways for people to connect with you and your services",
        icon: FiPackage,
      },
      {
        path: "/sessions/set-availabilities",
        title: "Set Your Availability",
        description: "Control when you're available for bookings to maintain work-life balance",
        icon: FiCalendar,
      },
      {
        path: "/schedule-session",
        title: "Publish Available Times",
        description: "Make your availability public so prospects can easily book with you",
        icon: FiClock,
        comingSoon: true
      },
      {
        path: "/sessions/client-booking-calendar",
        title: "My Scheduled Bookings",
        description: "See all your upcoming client sessions and prepare for each meeting",
        icon: FiCalendar,
      },
      {
        path: "/placeholder-for-dynamic-url",
        title: "Your Public Booking Page",
        description: "Share this link everywhere - your personal booking calendar for prospects to schedule with you",
        icon: FiCalendar,
      },
      {
        path: "/events/special",
        title: "Special Networking Events",
        description: "Host workshops, masterclasses, and networking events to attract multiple prospects at once",
        icon: FiCalendar,
        comingSoon: true
      },
    ],
  },

  clients: {
    title: "👥 Client Relationship Hub - Your CRM",
    subtitle: "Add your first people to your CRM and nurture relationships that drive your business",
    items: [
      {
        path: "/clients",
        title: "All Your Clients",
        description: "Your relationship database - track conversations, preferences, and opportunities with every contact",
        icon: FiUsers,
      },
      {
        path: "/newclient",
        title: "Add New Contact",
        description: "Start building your network by adding people you meet - from prospects to partners",
        icon: FiUserPlus,
      },
    ],
  },

  smsCampaigns: {
    title: "📱 SMS Marketing Campaigns - Stay Connected",
    subtitle: "Set up people you know on SMS campaigns that guide them to book sessions with you",
    items: [
      {
        path: "/sms/campaigns",
        title: "All SMS Campaigns",
        description: "View your nurture sequences - like 4-part series that guide contacts to book consultations",
        icon: FiMail,
        comingSoon: true
      },
      {
        path: "/sms/campaigns/create",
        title: "Create New Campaign",
        description: "Design SMS sequences that provide value and naturally lead to booking opportunities",
        icon: FiMail,
        comingSoon: true
      },
      {
        path: "/sms/campaigns/stats",
        title: "Campaign Performance",
        description: "Track which messages get responses and drive the most bookings",
        icon: FiBarChart2,
        comingSoon: true
      },
      {
        path: "/sms/templates",
        title: "Message Templates",
        description: "Pre-written messages for different situations - follow-ups, introductions, and offers",
        icon: FiFileText,
        comingSoon: true
      },
    ],
  },


  bills: {
    title: "💰 Service Billing - Get Paid for Your Work",
    subtitle: "When you're providing services in projects, create and send professional bills to get paid",
    items: [
      {
        path: "/bills",
        title: "All Invoices & Bills",
        description: "Track all your invoices - see what's paid, pending, and overdue for your service work",
        icon: FiFileText,
        comingSoon: true
      },
      {
        path: "/bills/new",
        title: "Create New Invoice",
        description: "Bill clients for consulting, development work, or any services you've provided",
        icon: FiDollarSign,
        comingSoon: true
      },
    ],
  },

  products: {
    title: "📦 Product Creation & Inventory - Build Together",
    subtitle: "When creating products together with clients, manage inventory and print professional labels",
    items: [
      {
        path: "/products/new",
        title: "Create New Product",
        description: "Add products you're creating with clients - from digital courses to physical goods",
        icon: FiPackage,
      },
      {
        path: "/print-labels",
        title: "Print Product Labels",
        description: "Create professional labels for physical products you're selling or shipping",
        icon: FiPrinter,
      },
      {
        path: "/products",
        title: "All Products & Inventory",
        description: "Manage your product catalog - track stock levels, pricing, and availability",
        icon: FiList,
      },
      {
        path: "/cart",
        title: "Shopping Cart Preview",
        description: "See how customers will experience buying your products online",
        icon: FiShoppingCart,
      },
    ],
  },
  
  websites: {
    title: "🌐 Website Development - Help Clients Online",
    subtitle: "If you're building websites for other clients, manage their projects and help set up their business",
    items: [
      {
        path: "/websites",
        title: "All Client Websites",
        description: "Track websites you're building for clients - from concept to launch",
        icon: FiServer,
        comingSoon: true
      },
      {
        path: "/websites/new",
        title: "Start New Website Project",
        description: "Begin a new website build for a client - plan their digital presence and business setup",
        icon: FiCode,
        comingSoon: true
      },
    ],
  },
  

  
 
  
  staticPages: {
    title: "Static Pages",
    items: [
      {
        path: "/",
        title: "Home Page",
        description: "View the main home page",
        icon: FiHome,
      },
      {
        path: "/about",
        title: "About Us",
        description: "About the organization",
        icon: FiUsers,
      },
      {
        path: "/services",
        title: "Services",
        description: "Our services page",
        icon: FiSettings,
      },
      {
        path: "/blog",
        title: "Blog",
        description: "Blog and announcements",
        icon: FiFileText,
      },
      {
        path: "/hardware-wallets",
        title: "Hardware Wallets",
        description: "Hardware wallet information page",
        icon: FiHardDrive,
      },
      {
        path: "/custom-website-development",
        title: "Custom Development",
        description: "Custom website development services",
        icon: FiCode,
      },
      {
        path: "/100members",
        title: "100 Members",
        description: "100 Members program page",
        icon: FiUsers,
      },
      {
        path: "/ecosystem-agreement",
        title: "Ecosystem Agreement",
        description: "Ecosystem agreement details",
        icon: FiFileText,
      },
      {
        path: "/welcome",
        title: "Welcome",
        description: "New member welcome page",
        icon: FiUsers,
      },
      {
        path: "/agreement", 
        title: "Agreement",
        description: "Legal agreements page",
        icon: FiFileText,
      },
      {
        path: "/founders",
        title: "Founders",
        description: "About the founders",
        icon: FiUsers,
      },
      {
        path: "/governance",
        title: "Governance",
        description: "Governance structure",
        icon: FiSettings,
      },
      {
        path: "/governing-rules",
        title: "Governing Rules",
        description: "Organizational rules",
        icon: FiSettings,
      },
      {
        path: "/intro-session",
        title: "Intro Session",
        description: "Introduction session information",
        icon: FiCalendar,
      },
    ],
  },
  
  testPages: {
    title: "Test Pages",
    items: [
      {
        path: "/test/sendemail",
        title: "Email Testing",
        description: "Test email functionality",
        icon: FiMail,
      },
      {
        path: "/test/upload-encrypted",
        title: "Encrypted Upload Test",
        description: "Test encrypted file uploads",
        icon: FiServer,
      },
      {
        path: "/test/upload-unencrypted",
        title: "Unencrypted Upload Test",
        description: "Test unencrypted file uploads",
        icon: FiServer,
      },
      {
        path: "/test/auth",
        title: "Auth Testing",
        description: "Test two-word authentication system",
        icon: FiLock,
      },
      {
        path: "/test/cart",
        title: "Cart Testing",
        description: "Test client cart functionality",
        icon: FiShoppingCart,
      },
      {
        path: "/test/upload-pinata",
        title: "Pinata Upload Test",
        description: "Test file uploads to Pinata",
        icon: FiServer,
      },
      {
        path: "/test/upload",
        title: "File Upload Test",
        description: "Test general file uploads",
        icon: FiServer,
      },
    ],
  },
  
  smsPodcastFunnel: {
    title: "SMS Podcast Funnel",
    items: [
      {
        path: "/property",
        title: "Property Landing Page",
        description: "SMS opt-in landing page for property investment podcast",
        icon: FiHeadphones,
      },
      {
        path: "/p1",
        title: "Episode 1 Page",
        description: "First episode with voting functionality",
        icon: FiTrendingUp,
      },
      {
        path: "/investment-results",
        title: "Investment Results",
        description: "Display voting results and next steps",
        icon: FiBarChart2,
      },
    ],
  },
  
  fileManagement: {
    title: "File Management",
    items: [
      {
        path: "/file/placeholder-hash",
        title: "View Files",
        description: "View uploaded files by hash",
        icon: FiDatabase,
      },
    ],
  },
};

const GET_CLIENT_STATS = gql`
  query GetClientStats {
    clientStats
  }
`;

const GET_PRODUCT_STATS = gql`
  query GetProductStats {
    productStats
  }
`;

const NavCard: React.FC<NavCardProps> = ({ item }) => {
  const navigate = useNavigate();
  
  const handleClick = () => {
    if (!item.comingSoon) {
      navigate(item.path);
    }
  };

  return (
    <Card
      as="button"
      onClick={handleClick}
      cursor={item.comingSoon ? "not-allowed" : "pointer"}
      _hover={{
        transform: item.comingSoon ? "none" : "translateY(-2px)",
        boxShadow: item.comingSoon ? "none" : "lg",
      }}
      opacity={item.comingSoon ? 0.6 : 1}
      transition="all 0.2s"
      position="relative"
    >
      <CardBody>
        <VStack align="start" spacing={2}>
          <Icon as={item.icon} boxSize={6} color={item.comingSoon ? "gray.400" : "blue.500"} />
          <Heading size="sm" color={item.comingSoon ? "gray.500" : "inherit"}>
            {item.title}
          </Heading>
          <Text fontSize="sm" color={item.comingSoon ? "gray.500" : "gray.600"}>
            {item.description}
          </Text>
          
          {item.comingSoon && (
            <Badge colorScheme="gray" mt={2}>
              Coming Soon
            </Badge>
          )}
        </VStack>
      </CardBody>
    </Card>
  );
};

const StatCard: React.FC<{
  label: string;
  mainValue: number;
  subValues: { label: string; value: number; link?: string; color?: string }[];
  helpText: string;
  icon: React.ComponentType;
  animate?: boolean;
  mainLink?: string;
  mainColor?: string;
}> = ({ label, mainValue, subValues, helpText, icon, animate = false, mainLink, mainColor = "blue.500" }) => {


  return (
    <Stat
      as={motion.div as any}
      initial={animate ? { scale: 0.9, opacity: 0 } : undefined}
      animate={animate ? { scale: 1, opacity: 1 } : undefined}
      transition="all 0.2s"
      px={{ base: 2, md: 4 }}
      py={"5"}
      shadow={"xl"}
      border={"1px solid"}
      borderColor={"gray.200"}
      rounded={"lg"}
    >
      <StatLabel fontWeight={"medium"} isTruncated>
        {label}
      </StatLabel>
      <StatNumber fontSize={"2xl"} fontWeight={"medium"}>
        <RouterLink 
          to={mainLink || "#"} 
          target="_blank" 
          rel="noopener noreferrer" 
          style={{ color: `var(--chakra-colors-${mainColor})` }}
        >
          {mainValue}
        </RouterLink>
        {subValues.map((subValue, index) => (
          <Text key={index} as="span" fontSize="sm" ml={2}>
            <Text as="span" color={subValue.color || "gray.500"}>
              {subValue.label}
            </Text>
            :{" "}
            {subValue.link ? (
              <RouterLink 
                to={subValue.link} 
                target="_blank" 
                rel="noopener noreferrer" 
                style={{ color: `var(--chakra-colors-${subValue.color || "gray.500"})` }}
              >
                {subValue.value}
              </RouterLink>
            ) : (
              <Text as="span" color={subValue.color || "gray.500"}>
                {subValue.value}
              </Text>
            )}
          </Text>
        ))}
      </StatNumber>
      <HStack justify="space-between">
        <StatHelpText>
          <HStack>
            <Icon as={icon} boxSize={4} />
            <Text>{helpText}</Text>
          </HStack>
        </StatHelpText>
        <StatHelpText>
          {/* <Text fontWeight="bold">
            Total: {total}
          </Text> */}
        </StatHelpText>
      </HStack>
    </Stat>
  );
};

const AdminDashboard = () => {
  const { isAuthenticated, user, loading } = useAuth();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();
  
  // GraphQL queries for stats
  const { loading: clientLoading, data: clientData, error: clientError } = useQuery(GET_CLIENT_STATS, {
    skip: !isAuthenticated || !user?.permissions?.includes("ADMIN"),
    fetchPolicy: "network-only"
  });
  
  const { loading: productLoading, data: productData, error: productError } = useQuery(GET_PRODUCT_STATS, {
    skip: !isAuthenticated || !user?.permissions?.includes("ADMIN"),
    fetchPolicy: "network-only"
  });

  // Show errors if queries fail
  useEffect(() => {
    if (clientError) {
      toast({
        title: "Error loading client statistics",
        description: clientError.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
    
    if (productError) {
      toast({
        title: "Error loading product statistics",
        description: productError.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  }, [clientError, productError, toast]);

  // Function to get personal booking calendar URL
  const getPersonalCalendarUrl = () => {
    return user?.id ? `/sessions/practitioner-calendar/${user.id}` : "#";
  };

  // Show login modal if not authenticated
  useEffect(() => {
    if (!loading && !isAuthenticated) {
      setIsLoginModalOpen(true);
    }
  }, [isAuthenticated, loading]);

  // Redirect if explicitly clicked logout from the login modal
  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
    if (!isAuthenticated) {
      navigate("/");
    }
  };

  // Loading state
  if (loading) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box minH="100vh" bg="gray.50" py={12}>
          <Container maxW="md">
            <Center h="50vh">
              <VStack spacing={6}>
                <Spinner size="xl" color="blue.500" thickness="4px" />
                <Text>Loading authentication information...</Text>
              </VStack>
            </Center>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </>
    );
  }

  // Not authenticated state
  if (!isAuthenticated) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box minH="100vh" bg="gray.50" py={12}>
          <Container maxW="md">
            <VStack spacing={8}>
              <Box bg="white" p={8} rounded="xl" shadow="lg" w="full">
                <VStack spacing={4}>
                  <Icon as={FiLock} boxSize={12} color="blue.500" />
                  <Heading size="lg">Authentication Required</Heading>
                  <Text textAlign="center">Please log in to access the admin dashboard.</Text>
                  <Button 
                    colorScheme="blue" 
                    onClick={() => setIsLoginModalOpen(true)}
                    leftIcon={<FiLock />}
                  >
                    Log In
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
        <LoginModal 
          isOpen={isLoginModalOpen} 
          onClose={handleLoginModalClose}
        />
        <FooterWithFourColumns />
      </>
    );
  }

  // No admin permission state
  if (!user?.permissions?.includes("ADMIN_DASHBOARD")) {
    return (
      <>
        <NavbarWithCallToAction />
        <Box minH="100vh" bg="gray.50" py={12}>
          <Container maxW="md">
            <VStack spacing={8}>
              <Box bg="white" p={8} rounded="xl" shadow="lg" w="full">
                <VStack spacing={4}>
                  <Icon as={FiAlertCircle} boxSize={12} color="red.500" />
                  <Heading size="lg" color="red.500">Access Denied</Heading>
                  <Text textAlign="center">You do not have permission to view this page.</Text>
                  <Text textAlign="center">Admin access is required.</Text>
                  <Button 
                    colorScheme="blue" 
                    onClick={() => navigate("/")}
                    leftIcon={<FiHome />}
                  >
                    Return to Home
                  </Button>
                </VStack>
              </Box>
            </VStack>
          </Container>
        </Box>
        <FooterWithFourColumns />
      </>
    );
  }

  // Admin dashboard content for authenticated admin users
  return (
    <>
      <NavbarWithCallToAction />
      <Container maxW="container.xl" py={8}>
        {/* Stats Section */}
        {/* <Box mb={8}>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {clientLoading ? (
              <Skeleton height="200px" />
            ) : (
              <StatCard
                label="Members"
                mainValue={clientData?.clientStats?.[0] || 0}
                subValues={[
                  { label: "24h", value: clientData?.clientStats?.[1] || 0, color: "purple.500" },
                  { label: "7 days", value: clientData?.clientStats?.[2] || 0, color: "green.500" },
                  { label: "30 days", value: clientData?.clientStats?.[3] || 0, color: "blue.500" }
                ]}
                helpText="Total registered clients"
                icon={FiUsers}
                animate={true}
              />
            )}
            
            {productLoading ? (
              <Skeleton height="200px" />
            ) : (
              <StatCard
                label="Products"
                mainValue={productData?.productStats?.[0] || 0}
                subValues={[
                  { label: "24h", value: productData?.productStats?.[1] || 0, color: "purple.500" },
                  { label: "7 days", value: productData?.productStats?.[2] || 0, color: "green.500" },
                  { label: "30 days", value: productData?.productStats?.[3] || 0, color: "blue.500" },
                  { label: "Active", value: productData?.productStats?.[4] || 0, color: "teal.500" },
                ]}
                helpText={`Physical: ${productData?.productStats?.[7] || 0} | Digital: ${productData?.productStats?.[8] || 0}`
              }
                icon={FiPackage}
                animate={true}
              />
            )}

            <StatCard
              label="Sales Today"
              mainValue={0}
              subValues={[
                { label: "7 days", value: 0, color: "green.500" },
                { label: "30 days", value: 0, color: "blue.500" }
              ]}
              helpText="Revenue in AUD"
              icon={FiDollarSign}
              animate={true}
              mainColor="green.500"
            />
          </SimpleGrid>
        </Box> */}

        {/* Business Overview Stats */}
        <Box mb={8}>
          <VStack align="start" spacing={4} mb={6}>
            <Heading size="lg">📊 Your Business at a Glance</Heading>
            <Text color="gray.600" fontSize="sm">
              Track your business growth across all activities - from client relationships to project delivery
            </Text>
          </VStack>
          
          <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
            <StatCard
              label="Active Clients"
              mainValue={clientData?.clientStats?.[0] || 0}
              subValues={[
                { label: "New this week", value: clientData?.clientStats?.[2] || 0, color: "green.500" },
              ]}
              helpText="Building relationships"
              icon={FiUsers}
              animate={true}
              mainColor="blue.500"
            />

            <StatCard
              label="Live Projects"
              mainValue={0}
              subValues={[
                { label: "Websites", value: 0, color: "purple.500" },
                { label: "Consulting", value: 0, color: "orange.500" },
              ]}
              helpText="Active collaborations"
              icon={FiBriefcase}
              animate={true}
              mainColor="purple.500"
            />

            <StatCard
              label="Bookings This Month"
              mainValue={0}
              subValues={[
                { label: "Sessions", value: 0, color: "teal.500" },
                { label: "Events", value: 0, color: "cyan.500" },
              ]}
              helpText="Networking activities"
              icon={FiCalendar}
              animate={true}
              mainColor="teal.500"
            />

            <StatCard
              label="Monthly Revenue"
              mainValue={0}
              subValues={[
                { label: "Invoiced", value: 0, color: "green.500" },
                { label: "Pending", value: 0, color: "yellow.500" },
              ]}
              helpText="Business income (AUD)"
              icon={FiDollarSign}
              animate={true}
              mainColor="green.500"
            />
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4} mt={4}>
            <StatCard
              label="Website Projects"
              mainValue={0}
              subValues={[
                { label: "In Development", value: 0, color: "blue.500" },
                { label: "Launched", value: 0, color: "green.500" },
              ]}
              helpText="Client web solutions"
              icon={FiCode}
              animate={true}
              mainColor="blue.500"
            />

            <StatCard
              label="SMS Campaigns"
              mainValue={0}
              subValues={[
                { label: "Active", value: 0, color: "purple.500" },
                { label: "Messages Sent", value: 0, color: "orange.500" },
              ]}
              helpText="Marketing outreach"
              icon={FiMail}
              animate={true}
              mainColor="purple.500"
            />

            <StatCard
              label="Products Created"
              mainValue={productData?.productStats?.[0] || 0}
              subValues={[
                { label: "Physical", value: productData?.productStats?.[7] || 0, color: "brown.500" },
                { label: "Digital", value: productData?.productStats?.[8] || 0, color: "cyan.500" },
              ]}
              helpText={`Active: ${productData?.productStats?.[4] || 0} products`}
              icon={FiPackage}
              animate={true}
              mainColor="orange.500"
            />
          </SimpleGrid>
        </Box>

        {/* Navigation Sections */}
        <VStack spacing={8} align="stretch">
          {Object.entries(navigationGroups).map(([key, group]) => (
            <Box key={key}>
              <VStack align="start" spacing={2} mb={6}>
                <Heading size="md">
                  {group.title}
                </Heading>
                {group.subtitle && (
                  <Text color="gray.600" fontSize="sm" maxW="2xl">
                    {group.subtitle}
                  </Text>
                )}
              </VStack>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                {group.items.map((item) => {
                  // Handle special case for personal calendar URL
                  const itemPath = item.title === "Your Public Booking Page" 
                    ? getPersonalCalendarUrl() 
                    : item.path;
                  
                  return (
                    <NavCard 
                      key={itemPath} 
                      item={{...item, path: itemPath}} 
                    />
                  );
                })}
              </SimpleGrid>
              <Divider mt={8} />
            </Box>
          ))}
        </VStack>
      </Container>
      <FooterWithFourColumns />
    </>
  );
};

export default AdminDashboard; 